import type { TTooltipPlacement } from "hooks/useTooltip";
import type { MouseEventHandler } from "react";
import type * as React from "react";

import { cn } from "@/lib/utils/cn";

import { LoadingSpinner } from "../LoadingSpinner";
import { Tooltip } from "../Tooltip";

export type TIconButtonProps = {
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  onClick?: ((event: React.MouseEvent) => Promise<void>) | MouseEventHandler;
  size?: TIconButtonSize;
  tooltip?: React.ReactNode;
  tooltipClassName?: string;
  tooltipPlacement?: TTooltipPlacement;
  variant?: TIconButtonVariant;
};
export type TIconButtonSize = "huge" | "large" | "medium" | "small" | "tiny" | "xl" | "xs";
export type TIconButtonVariant = "primary" | "secondary" | "sidebar";

const sizeClasses: Record<TIconButtonSize, string> = {
  huge: "rounded-md text-[32px]",
  large: "rounded-sm text-[24px] p-0.5",
  medium: "rounded-sm text-[20px] p-0.5",
  small: "rounded-sm text-[16px] p-0.5",
  tiny: "rounded-[2px] text-[12px] p-0",
  xl: "rounded-sm text-[24px] p-[3px]",
  xs: "rounded-[3px] text-[12px] p-0.5",
};

const variantClasses: Record<TIconButtonVariant, string> = {
  primary: "bg-transparent text-foreground hover:bg-primary/10 active:bg-primary/30",
  secondary: "bg-transparent text-foreground border border-border hover:bg-foreground/10 active:bg-foreground/30",
  sidebar: "bg-transparent text-sidebar-primary-foreground hover:bg-sidebar-foreground/15 active:bg-sidebar-primary",
};

/** @deprecated Use the new `Button` component from `components/ui/button` with an `icon` size. */
export const IconButton: FC<TIconButtonProps> = ({
  children,
  className,
  disabled = false,
  loading = false,
  onClick,
  ref,
  size = "medium",
  tooltip,
  tooltipClassName,
  tooltipPlacement,
  variant = "primary",
  ...restProps
}) => {
  return (
    <Tooltip className={tooltipClassName} content={tooltip} placement={tooltipPlacement} triggerRef={ref}>
      <button
        {...restProps}
        className={cn(
          "flex shrink-0 items-center justify-center border-0 p-1 transition-[background-color,border-color,color] duration-300 ease-out hover:cursor-pointer",
          sizeClasses[size],
          variantClasses[variant],
          disabled && "pointer-events-none transition-none text-muted",
          loading && "pointer-events-none",
          className,
        )}
        onClick={!disabled && !loading ? onClick : undefined}
        ref={ref}
        type="button"
      >
        {loading ? <LoadingSpinner /> : children}
      </button>
    </Tooltip>
  );
};
